import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { 
  LayoutDashboard, 
  Ticket, 
  ScanLine,
  ArrowRight,
  Loader2
} from 'lucide-react';
import Logo from '@/components/Logo';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppLauncher() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [userRoles, setUserRoles] = useState({
    isAdmin: false,
    hasTickets: false,
    isScanner: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else {
        detectUserRoles();
      }
    }
  }, [user, authLoading, navigate]);

  const detectUserRoles = async () => {
    try {
      // Check if user is admin/organizer
      const isAdmin = user.role === 'admin' || user.role === 'organizer' || user.role === 'super_admin';
      
      // Check if user has tickets (as guest)
      let hasTickets = false;
      try {
        const ticketsRes = await axios.get(`${API_URL}/tickets/my-tickets`);
        hasTickets = ticketsRes.data && ticketsRes.data.length > 0;
      } catch (error) {
        // User might not have tickets endpoint access
        hasTickets = false;
      }
      
      // Check if user is authorized as scanner
      let isScanner = false;
      try {
        const scannerRes = await axios.get(`${API_URL}/scanner/authorized-events`);
        isScanner = scannerRes.data && scannerRes.data.length > 0;
      } catch (error) {
        // User is not a scanner
        isScanner = false;
      }

      setUserRoles({
        isAdmin,
        hasTickets,
        isScanner
      });

      // Auto-redirect if user has only one role
      const rolesCount = [isAdmin, hasTickets, isScanner].filter(Boolean).length;
      if (rolesCount === 1) {
        if (isAdmin) navigate('/dashboard');
        else if (hasTickets) navigate('/my-tickets');
        else if (isScanner) navigate('/scanner');
      }
    } catch (error) {
      console.error('Error detecting user roles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-pink-900 flex items-center justify-center">
        <div className="text-center">
          <Logo size="xl" linkTo={null} className="mx-auto mb-6" />
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto" />
          <p className="text-white mt-4">Caricamento...</p>
        </div>
      </div>
    );
  }

  const availableRoles = [
    {
      id: 'admin',
      active: userRoles.isAdmin,
      icon: LayoutDashboard,
      title: 'Dashboard Admin',
      description: 'Gestisci i tuoi eventi e biglietti',
      color: 'from-purple-500 to-purple-700',
      path: '/dashboard'
    },
    {
      id: 'tickets',
      active: userRoles.hasTickets,
      icon: Ticket,
      title: 'I Miei Biglietti',
      description: 'Visualizza i biglietti acquistati',
      color: 'from-blue-500 to-blue-700',
      path: '/my-tickets'
    },
    {
      id: 'scanner',
      active: userRoles.isScanner,
      icon: ScanLine,
      title: 'Scanner QR',
      description: 'Scansiona biglietti per check-in',
      color: 'from-green-500 to-green-700',
      path: '/scanner'
    }
  ].filter(role => role.active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-pink-900">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <Logo size="xl" linkTo={null} className="mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-2">
            Benvenuto, {user?.name || 'Utente'}!
          </h1>
          <p className="text-white/80 text-lg">
            Scegli come vuoi utilizzare ZonePass
          </p>
        </div>

        {/* Role Cards */}
        {availableRoles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {availableRoles.map((role) => {
              const Icon = role.icon;
              return (
                <Card
                  key={role.id}
                  className="overflow-hidden hover:shadow-2xl transition-shadow cursor-pointer"
                  onClick={() => navigate(role.path)}
                >
                  <div className={`h-2 bg-gradient-to-r ${role.color}`}></div>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${role.color}`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{role.title}</h3>
                    <p className="text-gray-600">{role.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <p className="text-gray-600 mb-4">
                Non hai ancora accesso a nessuna funzionalità.
              </p>
              <button
                onClick={() => navigate('/')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Torna alla Home
              </button>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white transition-colors"
          >
            ← Torna alla home
          </button>
        </div>
      </div>
    </div>
  );
}
